import React from 'react'
import PropTypes from 'prop-types'
import { FormControl, FormHelperText, MenuItem, InputLabel, Input, Paper } from '@material-ui/core'
import { isMobile, isIOS } from 'react-device-detect'
import Downshift from 'downshift'
import callAllHandlers from '../utilities/callAllHandlers'

import styles from './AutoComplete.icss'

const propForItem = (prop, item) => (typeof prop === 'function' ? prop(item) : prop)
const propOrDefault = (prop, defaultValue) => (prop !== undefined ? prop : defaultValue)

class AutoComplete extends React.Component {
	constructor(props) {
		super(props)

		this.topRef = React.createRef()
		this.inputRef = React.createRef()

		this.state = {
			isOpen: false
		}

		this.inputOnFocus = this.inputOnFocus.bind(this)
		this.inputOnBlur = this.inputOnBlur.bind(this)
	}

	inputOnFocus() {
		this.props.onInputValueChange(this.props.value) // ensure the input value is sent out on first focus
		this.setState({ isOpen: true })
		if (isMobile) {
			this.setState({ scrollY: global.scrollY }) // save the scroll position
			setTimeout(() => this.topRef.current.scrollIntoView(isIOS ? true : { behavior: 'smooth' }), 0) // scroll the input to (near) the top
		}
	}

	inputOnBlur() {
		if (isMobile) {
			global.scrollTo(0, this.state.scrollY) // put the input back where it started
		}
		this.setState({ isOpen: false })
	}

	mergeInputProps({ onFocus, onBlur, ...rest }) {
		return {
			onFocus: callAllHandlers(onFocus, this.inputOnFocus),
			onBlur: callAllHandlers(onBlur, this.inputOnBlur),
			...rest
		}
	}

	render() {
		const { value, onSuggestionSelected, onInputValueChange, error, label, placeholder, suggestions, helperText, suggestionKey, suggestionValue, suggestionContent, ...inputProps } = this.props
		return (
			<Downshift
				inputValue={value}
				isOpen={this.state.isOpen}
				onSelect={(suggestion) => {
					if (onSuggestionSelected) {
						// as the value is in the store, selection will no be able to set the value directly, send it via a call to onChange
						onSuggestionSelected(suggestion)
					}
					this.inputRef.current.blur() // we have had to handle the open state to work around focus issues on ios, thus, we need to blur the input on select
				}}
				onStateChange={(changes) => {
					if (onInputValueChange) {
						if (Object.prototype.hasOwnProperty.call(changes, 'inputValue') && changes.type === Downshift.stateChangeTypes.changeInput) {
							// only call onInputValueChange if the user changed it, not for blur, select etc.
							onInputValueChange(changes.inputValue)
						}
					}
				}}
				itemToString={item => item && propOrDefault(propForItem(suggestionValue, item), item)}
			>
				{(downshift) => {
					const { inputRef, ...rootProps } = downshift.getRootProps({ refKey: 'inputRef', error }, { suppressRefError: true })
					return (
						<FormControl
							{...rootProps}
						>
							<div id="thetop" ref={this.topRef} className={isIOS ? null : styles.theTop} />
							<InputLabel {...downshift.getLabelProps()}>{label}</InputLabel>
							<Input
								inputRef={(node) => {
									this.inputRef.current = node
									inputRef(node)
								}}
								placeholder={placeholder}
								{...downshift.getInputProps(this.mergeInputProps(inputProps))}
							/>
							<div
								{...downshift.getMenuProps({
									className: styles.suggestionsContainerParent
								})}
							>
								{downshift.isOpen && suggestions && (
									<Paper className={styles.suggestionsContainer}>
										{suggestions.map((suggestion, index) => (
											<MenuItem
												{...downshift.getItemProps({
													item: suggestion,
													key: propOrDefault(propForItem(suggestionKey, suggestion), suggestion),
													selected: downshift.highlightedIndex === index,
													className: styles.suggestionsItem
												})}
											>
												{propOrDefault(propForItem(suggestionContent, suggestion), suggestion)}
											</MenuItem>
										))}
									</Paper>
								)}
							</div>
							{helperText && <FormHelperText>{helperText}</FormHelperText>}
						</FormControl>
					)
				}}
			</Downshift>
		)
	}
}

AutoComplete.propTypes = {
	value: PropTypes.string.isRequired,
	onSuggestionSelected: PropTypes.func,
	onInputValueChange: PropTypes.func,
	error: PropTypes.bool,
	label: PropTypes.string,
	placeholder: PropTypes.string,
	suggestions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	helperText: PropTypes.string,
	suggestionContent: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	suggestionKey: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	suggestionValue: PropTypes.oneOfType([PropTypes.func, PropTypes.string])
}

AutoComplete.defaultProps = {
	onSuggestionSelected: undefined,
	onInputValueChange: undefined,
	error: false,
	label: '',
	placeholder: '',
	suggestions: undefined,
	helperText: '',
	suggestionKey: undefined,
	suggestionContent: undefined,
	suggestionValue: undefined
}

export default AutoComplete



// WEBPACK FOOTER //
// ./src/components/AutoComplete.jsx